import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from "./ui/dialog";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";
import { fmtMDT } from "../lib/time";
import { cn } from "../lib/utils";
import {
  Sparkles, Play, RefreshCw, Loader2, CheckCircle2, AlertCircle,
  Globe, ChevronRight, Wrench, MessageSquare, FileDown, ShieldAlert,
} from "lucide-react";

const RISK_STYLE = {
  critical: "border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300",
  high:     "border-orange-500/40 bg-orange-500/10 text-orange-700 dark:text-orange-300",
  medium:   "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  low:      "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
};

const STATUS_MAP = {
  queued:   { icon: Loader2, cls: "text-muted-foreground", spin: false, label: "Queued" },
  running:  { icon: Loader2, cls: "text-sky-500",          spin: true,  label: "Running" },
  complete: { icon: CheckCircle2, cls: "text-emerald-500", spin: false, label: "Complete" },
  error:    { icon: AlertCircle, cls: "text-rose-500",     spin: false, label: "Error" },
};

function StatusChip({ status }) {
  const m = STATUS_MAP[status] || STATUS_MAP.queued;
  const Icon = m.icon;
  return (
    <span className={cn("inline-flex items-center gap-1 text-[11.5px] font-semibold", m.cls)}>
      <Icon className={cn("w-3.5 h-3.5", m.spin && "animate-spin")} />
      {m.label}
    </span>
  );
}

export default function AIPentester() {
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === "Super Admin" || (Array.isArray(user?.scopes) && user.scopes.includes("administrator"));
  const [runs, setRuns] = useState([]);
  const [allowlist, setAllowlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [launcherOpen, setLauncherOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [runsRes, allowRes] = await Promise.all([
        api.get("/vulnerability/agent/runs"),
        api.get("/vulnerability/allowlist"),
      ]);
      setRuns(runsRes.data.runs || []);
      setAllowlist(allowRes.data.allowlist || []);
    } catch (e) {
      toast({ title: "Couldn't load agent runs", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh while any run is queued/running.
  useEffect(() => {
    const anyBusy = runs.some((r) => r.status === "queued" || r.status === "running");
    if (!anyBusy) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [runs, load]);

  return (
    <section className="rounded-xl border border-border bg-card" data-testid="ai-pentester-section">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h3 className="text-[14.5px] font-semibold inline-flex items-center gap-1.5">
              AI pen-test agent
              <span className="text-[10px] rounded-full border border-primary/30 bg-primary/10 text-primary px-1.5 py-0.5 font-semibold uppercase tracking-wide">Beta</span>
            </h3>
            <p className="text-[11.5px] text-muted-foreground">Claude Sonnet drives our scanner tools autonomously. Allowlisted targets only.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} className="gap-1.5" data-testid="agent-refresh-btn">
            <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} /> Refresh
          </Button>
          {isAdmin && (
            <Button size="sm" onClick={() => setLauncherOpen(true)} className="gap-1.5" data-testid="agent-start-btn">
              <Play className="w-4 h-4" /> Start mission
            </Button>
          )}
        </div>
      </div>

      <div className="divide-y divide-border">
        {loading ? (
          [...Array(3)].map((_, i) => <div key={i} className="h-14 bg-muted/20 animate-pulse" />)
        ) : runs.length === 0 ? (
          <div className="p-10 text-center text-[13px] text-muted-foreground">
            No agent runs yet. {isAdmin ? "Click " : ""}<strong>{isAdmin ? "Start mission" : ""}</strong>{isAdmin ? " to kick one off." : "Ask an admin to launch a mission."}
          </div>
        ) : runs.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => setDetailId(r.id)}
            data-testid={`agent-run-row-${r.id}`}
            className="w-full text-left grid grid-cols-12 gap-3 items-center px-4 py-3 hover:bg-muted/40 transition-colors"
          >
            <div className="col-span-4 min-w-0">
              <div className="flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <div className="font-semibold text-[13.5px] truncate">{r.target}</div>
                {r.risk_score && (
                  <span className={cn("text-[10px] rounded-full border px-1.5 py-0.5 font-semibold uppercase tracking-wide", RISK_STYLE[r.risk_score] || "")}>
                    {r.risk_score}
                  </span>
                )}
              </div>
              <div className="text-[11px] text-muted-foreground truncate">{r.mission}</div>
            </div>
            <div className="col-span-3 text-[11.5px] text-muted-foreground truncate">{r.created_by || "system"}</div>
            <div className="col-span-2"><StatusChip status={r.status} /></div>
            <div className="col-span-2 text-[11.5px] text-muted-foreground">
              {r.events_count ?? 0} step{(r.events_count ?? 0) === 1 ? "" : "s"}
              <div className="text-[10.5px] opacity-70">{fmtMDT(r.finished_at || r.started_at || r.created_at)}</div>
            </div>
            <div className="col-span-1 text-right"><ChevronRight className="w-4 h-4 text-muted-foreground inline" /></div>
          </button>
        ))}
      </div>

      <AgentLauncher
        open={launcherOpen}
        onClose={() => setLauncherOpen(false)}
        allowlist={allowlist}
        onLaunched={() => { setLauncherOpen(false); load(); }}
      />
      <AgentRunDetail
        runId={detailId}
        open={!!detailId}
        onClose={() => setDetailId(null)}
      />
    </section>
  );
}

function AgentLauncher({ open, onClose, allowlist, onLaunched }) {
  const { toast } = useToast();
  const [target, setTarget] = useState("");
  const [mission, setMission] = useState("");
  const [busy, setBusy] = useState(false);

  const missionPresets = useMemo(() => [
    "Quick posture check: TLS, security headers, subdomain enum, then a Nuclei sweep on the apex. Summarise the top 3 things to fix.",
    "Deep audit: enumerate subdomains, http-probe the top 5, Nuclei-scan the apex + one high-value subdomain, flag anything critical.",
    "Header + TLS only — I need a fast go/no-go on our HTTPS config.",
  ], []);

  useEffect(() => {
    if (open) {
      setTarget(allowlist?.[0] || "");
      setMission(missionPresets[0]);
    }
  }, [open, allowlist, missionPresets]);

  const submit = async () => {
    if (!target.trim()) { toast({ title: "Enter a target", variant: "destructive" }); return; }
    if (!mission.trim()) { toast({ title: "Enter a mission", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await api.post("/vulnerability/agent/start", { target: target.trim(), mission: mission.trim() });
      toast({ title: "Mission started", description: `${target} — agent running in background.` });
      onLaunched?.();
    } catch (e) {
      toast({ title: "Mission rejected", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid="agent-launcher-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" /> Start AI pen-test mission
          </DialogTitle>
          <DialogDescription>
            The agent iteratively invokes scanner tools (TLS, headers, subfinder, Nuclei, http-probe) up to 12 steps.
            Every call is re-verified against the allowlist server-side.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="agent-target">Target host</Label>
            <Input id="agent-target" data-testid="agent-target-input" placeholder="iplogin.ca" value={target} onChange={(e) => setTarget(e.target.value)} />
            {allowlist.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {allowlist.map((a) => (
                  <button type="button" key={a} onClick={() => setTarget(a)} className="text-[11px] rounded-full border border-border px-2 py-0.5 hover:bg-muted">{a}</button>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="agent-mission">Mission brief</Label>
            <Textarea
              id="agent-mission"
              data-testid="agent-mission-input"
              rows={4}
              value={mission}
              onChange={(e) => setMission(e.target.value)}
              placeholder="Describe what you want the agent to look for…"
              className="text-[13px]"
            />
            <div className="flex flex-wrap gap-1.5">
              {missionPresets.map((p, i) => (
                <button
                  type="button"
                  key={i}
                  onClick={() => setMission(p)}
                  className="text-[11px] rounded-full border border-border px-2 py-0.5 hover:bg-muted"
                  data-testid={`mission-preset-${i}`}
                >
                  Preset {i + 1}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="gap-1.5" data-testid="agent-launch-submit-btn">
            <Play className="w-4 h-4" /> {busy ? "Starting…" : "Start mission"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AgentRunDetail({ runId, open, onClose }) {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!runId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/vulnerability/agent/runs/${runId}`);
      setData(data);
    } catch (e) {
      toast({ title: "Couldn't load run", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setLoading(false); }
  }, [runId, toast]);

  useEffect(() => { if (open && runId) load(); }, [open, runId, load]);
  useEffect(() => {
    const st = data?.run?.status;
    if (!open || (st !== "queued" && st !== "running")) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [open, data, load]);

  const downloadReport = async () => {
    try {
      const { data } = await api.get(`/vulnerability/scans/${runId}/report`, { responseType: "blob" });
      const blob = new Blob([data], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast({ title: "Report failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    }
  };

  const run = data?.run;
  const findings = data?.findings || [];
  const events = run?.events || [];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="agent-run-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-6">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="truncate">{run?.target || "Agent run"}</span>
            {run && <StatusChip status={run.status} />}
            {run?.risk_score && (
              <span className={cn("text-[10px] rounded-full border px-1.5 py-0.5 font-semibold uppercase tracking-wide", RISK_STYLE[run.risk_score])}>
                {run.risk_score}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>{run?.mission}</DialogDescription>
        </DialogHeader>

        {loading && !data ? (
          <div className="space-y-2 py-4">
            {[...Array(4)].map((_, i) => <div key={i} className="h-12 rounded-lg bg-muted/40 animate-pulse" />)}
          </div>
        ) : !run ? (
          <div className="py-8 text-center text-[13px] text-muted-foreground">No run.</div>
        ) : (
          <div className="space-y-4">
            {run.summary && (
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3.5">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-primary mb-1.5 inline-flex items-center gap-1.5">
                  <ShieldAlert className="w-3.5 h-3.5" /> Executive summary
                </div>
                <div className="text-[12.5px] whitespace-pre-wrap leading-relaxed">{run.summary}</div>
              </div>
            )}

            {run.error && (
              <div className="rounded-lg border border-destructive/40 bg-destructive/10 text-destructive text-[12px] p-2.5">
                {run.error}
              </div>
            )}

            {/* Event timeline */}
            <div>
              <div className="text-[12.5px] font-semibold mb-1.5">Agent trace ({events.length} event{events.length === 1 ? "" : "s"})</div>
              {events.length === 0 ? (
                <div className="rounded-lg border border-border p-4 text-center text-[12.5px] text-muted-foreground">
                  Waiting for first event…
                </div>
              ) : (
                <div className="space-y-1.5">
                  {events.map((ev, i) => <EventRow key={i} ev={ev} />)}
                </div>
              )}
            </div>

            {/* Findings */}
            {findings.length > 0 && (
              <div>
                <div className="text-[12.5px] font-semibold mb-1.5">Findings ({findings.length})</div>
                <div className="space-y-1.5">
                  {findings.map((f) => (
                    <div key={f.id} className="rounded-lg border border-border p-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={cn("text-[10.5px] rounded-full border px-2 py-0.5 font-semibold uppercase tracking-wide", RISK_STYLE[(f.severity || "").toLowerCase()] || "border-border bg-muted text-muted-foreground")}>
                          {f.severity || "info"}
                        </span>
                        <span className="text-[10.5px] rounded border border-border bg-muted/50 px-1.5 py-0.5 font-mono">{f.tool}</span>
                        <span className="text-[13px] font-medium">{f.name}</span>
                      </div>
                      {f.description && <div className="text-[12px] text-muted-foreground mt-1 line-clamp-3">{f.description}</div>}
                      <div className="text-[11px] text-muted-foreground mt-1.5 font-mono">{f.matched_at || f.host}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {run?.status === "complete" && (
            <Button variant="outline" onClick={downloadReport} className="gap-1.5 mr-auto" data-testid="agent-download-report-btn">
              <FileDown className="w-4 h-4" /> Download report
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EventRow({ ev }) {
  const isTool = ev.type === "tool_call";
  const isThought = ev.type === "thought";
  const isError = ev.type === "error";

  let icon, tone;
  if (isTool) { icon = Wrench; tone = "text-primary"; }
  else if (isThought) { icon = MessageSquare; tone = "text-muted-foreground"; }
  else if (isError) { icon = AlertCircle; tone = "text-rose-500"; }
  else if (ev.type === "finished") { icon = CheckCircle2; tone = "text-emerald-500"; }
  else { icon = Sparkles; tone = "text-muted-foreground"; }

  const Icon = icon;
  return (
    <div className="rounded-md border border-border p-2 text-[12px]">
      <div className="flex items-center gap-2">
        <Icon className={cn("w-3.5 h-3.5 shrink-0", tone)} />
        <span className="font-semibold">{isTool ? ev.name : ev.type}</span>
        {ev.duration_ms != null && <span className="text-[10.5px] text-muted-foreground">· {ev.duration_ms}ms</span>}
        {ev.at && <span className="text-[10.5px] text-muted-foreground ml-auto">{fmtMDT(ev.at)}</span>}
      </div>
      {isTool && ev.args && (
        <div className="text-[11px] font-mono text-muted-foreground mt-1 truncate">
          {Object.entries(ev.args).map(([k, v]) => `${k}=${JSON.stringify(v)}`).join(" · ")}
        </div>
      )}
      {isTool && ev.result_preview && (
        <details className="mt-1">
          <summary className="text-[11px] text-muted-foreground cursor-pointer">result</summary>
          <pre className="text-[11px] font-mono bg-muted/40 rounded p-1.5 mt-1 whitespace-pre-wrap break-all max-h-40 overflow-y-auto">
            {JSON.stringify(ev.result_preview, null, 2)}
          </pre>
        </details>
      )}
      {isThought && ev.text && (
        <div className="text-[11.5px] text-muted-foreground mt-1 whitespace-pre-wrap">{ev.text}</div>
      )}
      {ev.text && !isThought && ev.type !== "tool_call" && (
        <div className="text-[11.5px] text-muted-foreground mt-1 whitespace-pre-wrap">{ev.text}</div>
      )}
    </div>
  );
}
